import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { Mut } from '@efficiently/shared-util-types';

import {
	DEFAULT_MAX_SENTRY_EVENTS_PER_FINGERPRINT,
	DUPLICATE_REQUEST_MESSAGE,
} from '../config/duplicate-request-monitoring.constants';
import { DUPLICATE_REQUEST_MONITORING_OPTIONS } from '../config/duplicate-request-monitoring-options.token';
import { DuplicateRequestCandidate } from '../contracts/duplicate-request-candidate.interface';
import { DuplicateRequestMatch } from '../contracts/duplicate-request-match.interface';
import { DuplicateRequestReporter } from '../contracts/duplicate-request-reporter.interface';

type DuplicateRequestTagValue = string | number | boolean | null | undefined;

function toTags(tags: Record<string, DuplicateRequestTagValue>): Record<string, string> {
	return Object.entries(tags).reduce<Record<string, string>>((acc: Mut<Record<string, string>>, [key, value]) => {
		if (value !== null && value !== undefined && value !== '') {
			acc[key] = String(value);
		}

		return acc;
	}, {});
}

function getSafeCandidateData(candidate: DuplicateRequestCandidate): Record<string, unknown> {
	return {
		source: candidate.source,
		operationName: candidate.operationName,
		url: candidate.url,
		method: candidate.method,
		route: candidate.route,
		payloadShape: candidate.payloadShape,
	};
}

@Injectable({ providedIn: 'root' })
export class SentryDuplicateRequestReporterService implements DuplicateRequestReporter {
	private readonly options = inject(DUPLICATE_REQUEST_MONITORING_OPTIONS);
	// The counter is keyed by `groupingKey` (operation name / sanitized URL),
	// matching the Sentry fingerprint and Scope §4 of EFF-22582 ("N events per
	// fingerprint per session"). Keying by the candidate fingerprint (variables-
	// aware) would leak memory and let payload variance bypass the quota cap.
	private readonly sentryEventsByGroupingKey = new Map<string, number>();

	public report(match: DuplicateRequestMatch): void {
		const safeContext = this.getSafeContext(match);

		if (!this.options.production || !this.options.sentryEnabled) {
			// eslint-disable-next-line no-console
			console.warn('[DuplicateRequestMonitoring] duplicate request detected', safeContext);
			return;
		}

		const currentCount = this.sentryEventsByGroupingKey.get(match.current.groupingKey) ?? 0;
		const maxEvents = this.options.maxSentryEventsPerFingerprint ?? DEFAULT_MAX_SENTRY_EVENTS_PER_FINGERPRINT;

		if (currentCount >= maxEvents) {
			Sentry.addBreadcrumb({
				category: DUPLICATE_REQUEST_MESSAGE,
				message: match.current.groupingKey,
				level: 'warning',
				data: safeContext,
			});
			return;
		}

		this.sentryEventsByGroupingKey.set(match.current.groupingKey, currentCount + 1);

		Sentry.captureMessage(DUPLICATE_REQUEST_MESSAGE, {
			level: 'warning',
			fingerprint: [DUPLICATE_REQUEST_MESSAGE, match.current.groupingKey],
			tags: toTags({
				type: DUPLICATE_REQUEST_MESSAGE,
				app: this.options.app,
				environment: this.options.environmentName,
				source: match.current.source,
				operationName: match.current.operationName,
				url: match.current.url,
				method: match.current.method,
				route: match.current.route,
				reason: match.reason,
			}),
			contexts: {
				duplicateRequest: safeContext,
			},
		});
	}

	private getSafeContext(match: DuplicateRequestMatch): Record<string, unknown> {
		return {
			reason: match.reason,
			gapMs: match.gapMs,
			app: this.options.app,
			route: match.current.route,
			previous: getSafeCandidateData(match.previous),
			current: getSafeCandidateData(match.current),
		};
	}
}
